import React, { useCallback, useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { apptApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Table, Pager, Tabs, StatusBadge } from "../../Components/UI/ui";
import { GATE, STATUS_ORDER, STATUS_LABEL, todayStr, addDays, fmtDateTime, cap, money } from "../../lib/lab";
import AppointmentForm from "./AppointmentForm";
import AppointmentDetail from "./AppointmentDetail";

// Preset views from §5 of the appointments doc
const VIEWS = [
  { id: "all", label: "All" },
  { id: "referrals", label: "Referral inbox", params: { status: "REQUESTED" } },
  { id: "home", label: "Home collection", params: { collectionType: "home", status: "CONFIRMED" } },
  { id: "reports", label: "Reports due", params: { status: "IN_PROCESS,REPORT_READY" } },
];

const Appointments = () => {
  const { can } = useAuth();
  const { toast } = useApp();
  const [view, setView] = useState("all");
  const [startDate, setStartDate] = useState(addDays(todayStr(), -7));
  const [endDate, setEndDate] = useState(addDays(todayStr(), 30));
  const [status, setStatus] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ appointments: [], totalCount: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const preset = VIEWS.find((v) => v.id === view)?.params || {};
      const params = { page, limit: 25, ...preset };
      // Referral inbox & home-collection views are not date bound
      if (view === "all" || view === "reports") { params.startDate = startDate; params.endDate = endDate; }
      if (status && view === "all") params.status = status;
      if (serviceType) params.serviceType = serviceType;
      if (search.trim()) params.search = search.trim();
      setData(await apptApi.list(params));
    } catch (e) {
      toast.error(e);
    } finally {
      setLoading(false);
    }
  }, [view, page, startDate, endDate, status, serviceType, search, toast]);

  useEffect(() => {
    const h = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, search]);

  useEffect(() => { setPage(1); }, [view, startDate, endDate, status, serviceType, search]);

  const columns = [
    { key: "scheduledAt", title: "When", render: (r) => <span>{fmtDateTime(r.scheduledAt)}</span> },
    { key: "patientName", title: "Patient", render: (r) => (<><div className="primary">{r.patientName}</div><div className="sub">{r.patientPhone}{r.sampleId ? ` · ${r.sampleId}` : ""}</div></>) },
    { key: "items", title: "Items", render: (r) => (<><div>{(r.items || []).map((i) => i.name).join(", ")}</div><div className="sub">{cap(r.serviceType)}{r.collectionType === "home" ? " · home collection" : ""}</div></>) },
    { key: "source", title: "Source", render: (r) => r.source === "doctor_referral" ? <span>{r.referral?.doctorName || "Doctor"}<div className="sub">referral</div></span> : <span className="sub">Lab</span> },
    { key: "amount", title: "Amount", align: "right", render: (r) => money(r.amount?.total) },
    { key: "paymentStatus", title: "Payment", render: (r) => <Badge kind={r.paymentStatus}>{cap(r.paymentStatus || "unpaid")}</Badge> },
    { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <Page
      title="Bookings"
      actions={can(...GATE.book) && <Button variant="primary" onClick={() => setBookOpen(true)}><IoMdAdd /> New booking</Button>}
    >
      <Tabs tabs={VIEWS} value={view} onChange={setView} />
      <div className="lab-toolbar">
        <input className="lab-input grow" placeholder="Search patient name or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
        {(view === "all" || view === "reports") && (
          <>
            <input type="date" className="lab-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span className="lab-muted">to</span>
            <input type="date" className="lab-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </>
        )}
        {view === "all" && (
          <select className="lab-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        )}
        <select className="lab-select" value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
          <option value="">All services</option>
          <option value="pathology">Pathology</option>
          <option value="radiology">Radiology</option>
        </select>
      </div>

      <div className="lab-page__body">
        <Table columns={columns} rows={data.appointments || []} loading={loading} onRowClick={(r) => setSelected(r._id)} empty="No bookings match these filters" />
      </div>
      <Pager page={page} totalPages={data.totalPages} total={data.totalCount} onChange={setPage} />

      <AppointmentForm open={bookOpen} onClose={() => setBookOpen(false)} onSaved={load} />
      <AppointmentDetail id={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </Page>
  );
};

export default Appointments;
